import React, { useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Divider,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowForward,
  Favorite,
  Security,
  Speed,
  Palette
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { LanguageContext } from '../../localization';
import tokens from '../../theme/tokens';

const Footer = ({ variant = 'landing', onGetStarted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  
  // Safe access to language context
  const languageContext = useContext(LanguageContext);
  
  // Create fallback translation function
  const t = languageContext?.t || ((key) => {
    const fallbackTexts = {
      'footer.copyright': 'CV4me - Free CV Builder',
      'footer.features.privateSecure': 'Private & Secure',
      'footer.features.readyInMinutes': 'Ready in Minutes',
      'footer.features.professionalTemplates': 'Professional Templates'
    };
    return fallbackTexts[key] || key;
  });

  const handleLegalPageNavigation = (path) => {
    navigate(path, { state: { from: location.pathname } });
  };

  // App footer - very minimal and subtle, overlapping
  if (variant === 'app') {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'transparent',
          py: 1,
          px: 2,
          zIndex: 1,
          pointerEvents: 'none' // Allow clicks to pass through to underlying content
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            width: '100%'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: tokens.colors.text.secondary,
              fontSize: '0.75rem',
              textAlign: 'left',
              pointerEvents: 'auto', // Re-enable pointer events for text
              backgroundColor: 'rgba(255, 255, 255, 0.8)', // Subtle background for readability
              px: 1,
              py: 0.5,
              borderRadius: '4px'
            }}
          >
            © {currentYear} {t('footer.copyright')}
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            pointerEvents: 'auto' // Re-enable pointer events for links
          }}>
            <Link
              component="button"
              onClick={() => handleLegalPageNavigation('/datenschutz')}
              sx={{
                color: tokens.colors.text.secondary,
                textDecoration: 'none',
                fontSize: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.8)', // Subtle background for readability
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: tokens.colors.primary.main,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              {t('footer.legal.privacy')}
            </Link>
            <Link
              component="button"
              onClick={() => handleLegalPageNavigation('/nutzungsbedingungen')}
              sx={{
                color: tokens.colors.text.secondary,
                textDecoration: 'none',
                fontSize: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.8)', // Subtle background for readability
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: tokens.colors.primary.main,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              {t('footer.legal.terms')}
            </Link>
            <Link
              component="button"
              onClick={() => handleLegalPageNavigation('/impressum')}
              sx={{
                color: tokens.colors.text.secondary,
                textDecoration: 'none',
                fontSize: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.8)', // Subtle background for readability
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: tokens.colors.primary.main,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              {t('footer.legal.impressum')}
            </Link>
          </Box>
        </Box>
      </Box>
    );
  }

  // Landing page footer - full featured
  return (
    <Box
      sx={{
        backgroundColor: tokens.colors.neutral[900],
        color: 'white',
        position: 'relative'
      }}
    >
      <Container maxWidth="lg">
        {/* Final CTA Section */}
        <Box
          sx={{
            py: { xs: 4, md: 12 },
            textAlign: 'center',
            borderBottom: `1px solid ${tokens.colors.neutral[700]}`
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.6rem', md: '2.5rem', lg: '3rem' },
              fontWeight: tokens.typography.fontWeights.bold,
              color: 'white',
              mb: { xs: 2, md: 3 }
            }}
          >
            {t('footer.cta.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.9rem', md: '1.1rem' },
              color: tokens.colors.neutral[300],
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
              mb: { xs: 3, md: 6 }
            }}
          >
            {t('footer.cta.subtitle')}
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={onGetStarted}
            endIcon={<ArrowForward />}
            sx={{
              backgroundColor: tokens.colors.primary.main,
              color: 'white',
              fontWeight: tokens.typography.fontWeights.semibold,
              textTransform: 'none',
              px: { xs: 4, md: 6 },
              py: { xs: 1.5, md: 2 },
              fontSize: { xs: '1rem', md: '1.2rem' },
              borderRadius: tokens.radius.base,
              boxShadow: tokens.shadows.lg,
              '&:hover': {
                backgroundColor: tokens.colors.primary.dark,
                boxShadow: tokens.shadows.elegant,
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('footer.cta.button')}
          </Button>

          {/* Trust indicators */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            justifyContent: 'center', 
            gap: 4, 
            flexWrap: 'wrap', 
            mt: 4 
          }}>
            <Typography
              variant="body2"
              sx={{
                color: tokens.colors.neutral[400],
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Security sx={{ fontSize: 16 }} />
              {t('footer.features.privateSecure')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: tokens.colors.neutral[400],
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Speed sx={{ fontSize: 16 }} />
              {t('footer.features.readyInMinutes')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: tokens.colors.neutral[400],
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Palette sx={{ fontSize: 16 }} />
              {t('footer.features.professionalTemplates')}
            </Typography>
          </Box>
        </Box>

        {/* Footer Content */}
        <Box sx={{ py: { xs: 3, md: 6 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: tokens.typography.fontWeights.bold,
              color: tokens.colors.primary.main,
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            privacv
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: tokens.colors.neutral[300],
              lineHeight: 1.6,
              mb: { xs: 2, md: 3 },
              maxWidth: '600px',
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            {t('footer.description')}
          </Typography>
          
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            alignItems: 'center', 
            gap: 1 
          }}>
            <Typography
              variant="body2"
              sx={{ color: tokens.colors.neutral[400] }}
            >
              {t('footer.madeWith')}
            </Typography>
            <Favorite sx={{ fontSize: 16, color: tokens.colors.error }} />
            <Typography
              variant="body2"
              sx={{ color: tokens.colors.neutral[400] }}
            >
              {t('footer.forJobSeekers')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: tokens.colors.neutral[700] }} />

        {/* Bottom Footer */}
        <Box
          sx={{
            py: { xs: 2.5, md: 4 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: tokens.colors.neutral[400],
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            © {currentYear} {t('footer.copyright')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link
              component="button"
              onClick={() => handleLegalPageNavigation('/datenschutz')}
              sx={{
                color: tokens.colors.neutral[400],
                textDecoration: 'none',
                fontSize: '0.85rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: tokens.colors.primary.main
                }
              }}
            >
              {t('footer.legal.privacy')}
            </Link>
            <Link
              component="button"
              onClick={() => handleLegalPageNavigation('/nutzungsbedingungen')}
              sx={{
                color: tokens.colors.neutral[400],
                textDecoration: 'none',
                fontSize: '0.85rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: tokens.colors.primary.main
                }
              }}
            >
              {t('footer.legal.terms')}
            </Link>
            <Link
              component="button"
              onClick={() => handleLegalPageNavigation('/impressum')}
              sx={{
                color: tokens.colors.neutral[400],
                textDecoration: 'none',
                fontSize: '0.85rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: tokens.colors.primary.main
                }
              }}
            >
              {t('footer.legal.impressum')}
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 